import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Stack, alpha, useTheme } from '@mui/material';
import SectionWrapper from '../SectionWrapper';

const CODE_SNIPPET = `class SelfAttention(nn.Module):
    def __init__(self, d_model):
        super().__init__()
        self.d_model = d_model

    def forward(self, q, k, v):
        # A Matemática da Moderna Inteligência Artificial
        w_score = torch.matmul(q, k.transpose(-2, -1))
        w_score = w_score / math.sqrt(self.d_model)
        
        attn = F.softmax(w_score, dim=-1)
        return torch.matmul(attn, v)
        
    # Transformando ruído em significado...`;

const CODE_TYPING_SPEED_MS = 52;
const CODE_LOOP_DELAY_MS = 10000;

/**
 * OVERVIEW HERO COMPONENT - V7 (REMASTERED)
 * Concept: "Swiss Space Agency".
 * Characteristics: Ultra-bold typography, intense whitespace (even in dark mode), mathematical precision.
 */
const OverviewHero = () => {
    const theme = useTheme();

    const [displayedCode, setDisplayedCode] = useState('');

    useEffect(() => {
        let isMounted = true;
        let typingInterval = null;
        let loopTimeout = null;

        const startTypingLoop = () => {
            let index = 0;
            if (!isMounted) return;
            setDisplayedCode('');

            typingInterval = setInterval(() => {
                if (!isMounted) return;

                if (index < CODE_SNIPPET.length) {
                    index += 1;
                    setDisplayedCode(CODE_SNIPPET.slice(0, index));
                    return;
                }

                clearInterval(typingInterval);
                loopTimeout = setTimeout(() => {
                    startTypingLoop();
                }, CODE_LOOP_DELAY_MS);
            }, CODE_TYPING_SPEED_MS);
        };

        startTypingLoop();

        return () => {
            isMounted = false;
            clearInterval(typingInterval);
            clearTimeout(loopTimeout);
        };
    }, []);

    // Simple syntax highlighter
    const getHighlightedHtml = (code) => {
        const lines = code.split('\n');
        return lines.map(line => {
            let safeLine = line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

            const commentIndex = safeLine.indexOf('#');
            if (commentIndex !== -1) {
                const codePart = safeLine.substring(0, commentIndex);
                const commentPart = safeLine.substring(commentIndex);
                return processTokenColors(codePart) + `<span style="color: rgba(200, 204, 214, 0.28)">${commentPart}</span>`;
            }

            return processTokenColors(safeLine);
        }).join('<br/>');
    };

    const processTokenColors = (str) => {
        // Highlighting for OverviewHero (Generic styles adjusted for context)
        // Keywords: class, def, return -> Primary/Red
        let formatted = str.replace(
            /\b(class|def|return)\b/g,
            `<span style="color: ${alpha(theme.palette.primary.main, 0.62)}">$1</span>`
        );

        // Key Classes/Functions -> White/Bright
        formatted = formatted.replace(
            /\b(SelfAttention|__init__|forward|super|transpose|sqrt|matmul|softmax)\b/g,
            '<span style="color: rgba(238, 241, 247, 0.76)">$1</span>'
        );

        return formatted;
    };

    return (
        <SectionWrapper
            id="hero-v7"
            className="section-hero"
            sx={{
                bgcolor: '#050505',
                color: 'common.white',
            }}
        >
            <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                py: 4 // Reduced safe zone inside wrapper
            }}>
                {/* AMBIENT LIGHTING (No textures, just pure light) */}
                <Box sx={{
                    position: 'absolute', top: '-20%', right: '-10%',
                    width: '60vw', height: '60vw',
                    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 70%)`,
                    filter: 'blur(100px)',
                    zIndex: 0
                }} />

                <Container maxWidth={false} sx={{ maxWidth: '1200px', position: 'relative', zIndex: 2 }}>
                    <Stack spacing={6}>
                        {/* SUPER TITLE */}
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    fontSize: '1.1rem',
                                    color: 'primary.main',
                                    letterSpacing: '0.3em',
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 2,
                                    opacity: 0.9
                                }}
                            >
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                                    01 Nova Engenharia de Soluções
                                </Box>
                            </Typography>
                        </Box>

                        {/* HEADLINE */}
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: 'clamp(2.5rem, 10vw, 3.5rem)', md: '7rem', lg: '8.5rem' }, // Responsive clamp
                                fontWeight: 800,
                                lineHeight: 0.95,
                                letterSpacing: '-0.04em',
                                color: 'common.white',
                                mb: 4,
                                wordBreak: 'break-word' // Safety net
                            }}
                        >
                            Design. <br />
                            <Box component="span" sx={{ color: 'grey.800' }}>Dados.</Box> <br />
                            Inteligência.
                        </Typography>

                        {/* SUBTEXT WITH STRUCTURE */}
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={8} alignItems={{ md: 'flex-start' }} sx={{ mt: 4 }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: 'grey.400',
                                    maxWidth: '600px',
                                    fontWeight: 300,
                                    fontSize: { xs: '1.2rem', md: '1.6rem' },
                                    lineHeight: 1.4
                                }}
                            >
                                Não criamos apenas software. Desenvolvemos soluções digitais que geram impacto real nas organizações.
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* DECORATIVE CODE SNIPPET - "THE ENGINE" */}
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        display: { xs: 'none', lg: 'block' },
                        opacity: 0.42,
                        pointerEvents: 'none',
                        zIndex: 0,
                        textAlign: 'left',
                    }}>
                        <Box
                            sx={{
                                fontFamily: '"Fira Code", "Roboto Mono", monospace',
                                fontSize: '0.85rem',
                                color: 'rgba(210, 214, 223, 0.62)',
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap'
                            }}
                            dangerouslySetInnerHTML={{
                                __html: getHighlightedHtml(displayedCode) + '<span style="color: rgba(236, 239, 245, 0.62); opacity: 1; animation: blink-cursor 1.45s infinite">|</span>'
                            }}
                        />
                    </Box>

                    <style>
                        {`
                        @keyframes blink-cursor {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0; }
                        }
                        `}
                    </style>
                </Container>
            </Box>
        </SectionWrapper>
    );
};

export default OverviewHero;
